import { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import { Document, Page } from 'react-pdf';
import usePastPapers from '@/api/usePastPapers.ts';
import supabaseClient from '@/api/supabaseClient.ts';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import useCreateAttempt from '@/api/useCreateAttempt.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { formatPastPaperTitle } from '@/utils/formatPastPaperTitle';
import useInProgressPaperAttempt from '@/api/useInProgressPaperAttempt';
import InProgressBanner from '@/components/ui/InProgressBanner/InProgressBanner';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';
import { BookOpenIcon, DocumentTextIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

type Subject = 'English Language' | 'English Literature';
type PaperNumber = 'Paper 1' | 'Paper 2';

interface SelectionState {
  subject: Subject | null;
  paperNumber: PaperNumber | null;
  paperId: string | null;
}

const FullPastPapersPage: FC = () => {
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const [selection, setSelection] = useState<SelectionState>({
    subject: null,
    paperNumber: null,
    paperId: null,
  });
  const [numPages, setNumPages] = useState<number>(0);
  const [limitError, setLimitError] = useState<string | null>(null);

  // Fetch past papers from database
  const { data: pastPapers = [], isLoading } = usePastPapers();

  // Check for in-progress paper attempt
  const { data: inProgressAttempt } = useInProgressPaperAttempt(session?.user?.id);

  // Create attempt mutation
  const createAttemptMutation = useCreateAttempt();

  // Filter papers based on current selection
  const filteredPapers = useMemo(() => {
    if (!selection.subject || !selection.paperNumber) return [];
    return pastPapers
      .filter(
        (paper) => paper.subject === selection.subject && paper.paper_number === selection.paperNumber
      )
      .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }, [pastPapers, selection.subject, selection.paperNumber]);

  // Get available subjects and papers from data
  const availableSubjects = useMemo(() => {
    const subjects = new Set(pastPapers.map((p) => p.subject).filter(Boolean));
    return Array.from(subjects) as Subject[];
  }, [pastPapers]);

  const availablePaperNumbers = useMemo(() => {
    if (!selection.subject) return [];
    const papers = new Set(
      pastPapers.filter((p) => p.subject === selection.subject).map((p) => p.paper_number).filter(Boolean)
    );
    return Array.from(papers) as PaperNumber[];
  }, [pastPapers, selection.subject]);

  const handleSelectSubject = (subject: Subject) => {
    setSelection({ subject, paperNumber: null, paperId: null });
    setNumPages(0);
  };

  const handleSelectPaperNumber = (paperNumber: PaperNumber) => {
    setSelection((prev) => ({ ...prev, paperNumber, paperId: null }));
    setNumPages(0);
  };

  const handleSelectPaper = (paperId: string) => {
    setSelection((prev) => ({ ...prev, paperId }));
    setNumPages(0);
  };

  const handleBack = () => {
    if (selection.paperId) {
      setSelection((prev) => ({ ...prev, paperId: null }));
      setNumPages(0);
    } else if (selection.paperNumber) {
      setSelection((prev) => ({ ...prev, paperNumber: null, paperId: null }));
    } else if (selection.subject) {
      setSelection({ subject: null, paperNumber: null, paperId: null });
    } else {
      navigate(Routes.DASHBOARD);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handleStartPaper = async () => {
    if (!session?.user?.id || !selection.paperId) return;

    setLimitError(null); // Clear previous errors

    try {
      const attempt = await createAttemptMutation.mutateAsync({
        authId: session.user.id,
        pastPaperId: selection.paperId,
      });

      // Navigate to take paper page
      navigate(`/paper-attempts/${attempt.id}`);
    } catch (error: unknown) {
      console.error('Failed to create attempt:', error);

      // Extract error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to create paper attempt. Please try again.';

      setLimitError(errorMessage);
    }
  };

  // Find selected paper data
  const selectedPaperData = useMemo(
    () => pastPapers.find((paper) => paper.id === selection.paperId),
    [pastPapers, selection.paperId]
  );

  // Get page title based on selection state
  const getPageTitle = () => {
    if (selection.paperId && selectedPaperData) {
      return formatPastPaperTitle(selectedPaperData);
    }
    if (selection.paperNumber) {
      return `${selection.subject} - ${selection.paperNumber}`;
    }
    if (selection.subject) {
      return selection.subject;
    }
    return 'Sit a Past Paper';
  };

  // Generate the public URL for the PDF from the filename
  const pdfUrl = useMemo(() => {
    if (!selectedPaperData?.pdf_url) return '';

    // pdf_url now contains just the filename (e.g., "past-paper-edexcel-english-paper1-june-2024.pdf")
    // Generate the full public URL using Supabase client
    const { data } = supabaseClient.storage.from('past-papers').getPublicUrl(selectedPaperData.pdf_url);

    return data.publicUrl;
  }, [selectedPaperData]);

  // Render subject selection cards
  const renderSubjectSelection = () => (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1">
        <h2 className="text-lg font-semibold mb-4">Choose a Subject</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* English Language Card */}
          <button
            onClick={() => handleSelectSubject('English Language')}
            disabled={!availableSubjects.includes('English Language')}
            className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <DocumentTextIcon className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">English Language</h3>
                <p className="text-sm text-gray-500 mt-1">1EN0 - Edexcel GCSE</p>
                <p className="text-xs text-gray-400 mt-2">
                  {pastPapers.filter((p) => p.subject === 'English Language').length} papers available
                </p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
            </div>
          </button>

          {/* English Literature Card */}
          <button
            onClick={() => handleSelectSubject('English Literature')}
            disabled={!availableSubjects.includes('English Literature')}
            className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpenIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">English Literature</h3>
                <p className="text-sm text-gray-500 mt-1">1ET0 - Edexcel GCSE</p>
                <p className="text-xs text-gray-400 mt-2">
                  {pastPapers.filter((p) => p.subject === 'English Literature').length} papers available
                </p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
            </div>
          </button>
        </div>
      </div>

      {/* Information Box */}
      <div className="lg:w-[450px] bg-primary rounded-lg p-6 text-white">
        <h3 className="text-lg font-bold mb-3">Before You Start a Past Paper</h3>

        <p className="font-semibold mb-3">Please check the following before you begin:</p>

        <ul className="space-y-2 mb-4 text-sm leading-relaxed">
          <li>
            * Make sure you are already achieving your predicted or aspirational grade in the{' '}
            <strong>**Practice Area**</strong> before attempting a full past paper.
          </li>
          <li>
            * Allow enough time to complete the <strong>**entire paper in one go**</strong> – it will be timed
            and you <strong>**won't be able to pause**</strong> the timer.
          </li>
          <li>
            * You can exit and return to the past paper later, but it will no longer be a true exam-style
            attempt.
          </li>
          <li>
            * Have all the <strong>**equipment**</strong> you need ready (e.g. pens, calculator, highlighters,
            rough paper).
          </li>
          <li>
            * Ensure you can work in <strong>**quiet, exam-style conditions**</strong>, without interruptions.
          </li>
        </ul>

        <p className="font-semibold mb-2">After you finish:</p>

        <ul className="space-y-2 mb-3 text-sm leading-relaxed">
          <li>
            * Your paper will be <strong>**AI-marked**</strong> with full feedback at the end of the exam.
          </li>
          <li>
            * If you score <strong>**below 70%**</strong>, our AI will identify your weak areas and
            automatically generate a new <strong>**Practice Set**</strong> so you can focus your revision where
            it's needed most.
          </li>
          <li>
            * You may want to print out a hard copy of the reading extract so you can refer to it while sitting
            the exam. To do this, go to the 'Download Past Papers' section and download / print the reading
            extract for the paper you are about to sit.
          </li>
        </ul>

        <p className="font-bold">**Good luck – you've got this!**</p>
      </div>
    </div>
  );

  // Render paper number selection cards
  const renderPaperNumberSelection = () => (
    <div>
      <h2 className="text-lg font-semibold mb-4">Choose a Paper</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        {/* Paper 1 Card */}
        <button
          onClick={() => handleSelectPaperNumber('Paper 1')}
          disabled={!availablePaperNumbers.includes('Paper 1')}
          className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-blue-600">P1</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">Paper 1</h3>
              <p className="text-sm text-gray-500 mt-1">
                {selection.subject === 'English Language' ? 'Fiction & Imaginative Writing' : 'Shakespeare & Post-1914 Literature'}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {pastPapers.filter((p) => p.subject === selection.subject && p.paper_number === 'Paper 1').length} papers available
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
          </div>
        </button>

        {/* Paper 2 Card */}
        <button
          onClick={() => handleSelectPaperNumber('Paper 2')}
          disabled={!availablePaperNumbers.includes('Paper 2')}
          className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-green-600">P2</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">Paper 2</h3>
              <p className="text-sm text-gray-500 mt-1">
                {selection.subject === 'English Language' ? 'Non-Fiction & Transactional Writing' : '19th Century Novel & Poetry'}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {pastPapers.filter((p) => p.subject === selection.subject && p.paper_number === 'Paper 2').length} papers available
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
          </div>
        </button>
      </div>
    </div>
  );

  // Render paper list
  const renderPaperList = () => (
    <div>
      <h2 className="text-lg font-semibold mb-4">Select a Past Paper</h2>
      {isLoading ? (
        <p className="text-gray-500">Loading papers...</p>
      ) : filteredPapers.length === 0 ? (
        <p className="text-gray-500">No papers available for this selection.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPapers.map((paper) => {
            const date = paper.date ? new Date(paper.date) : null;
            const month = date?.toLocaleString('en-US', { month: 'long' }) || '';
            const year = date?.getFullYear() || '';

            return (
              <button
                key={paper.id}
                onClick={() => handleSelectPaper(paper.id)}
                className="group bg-white border-2 border-gray-200 rounded-xl p-5 text-left transition-all hover:border-primary hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary">
                      {month} {year}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{paper.title || 'Past Paper'}</p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render paper preview with start button
  const renderPaperPreview = () => (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="lg:flex-1">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-lg font-semibold">Paper Preview</h2>
          <LoadingButton
            loading={createAttemptMutation.isPending}
            color="primary"
            className="border-none text-white bg-primary"
            onClick={handleStartPaper}
            disabled={!!inProgressAttempt}
          >
            Start Paper
          </LoadingButton>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title={getPageTitle()}
        showBackButton
        onBackClick={handleBack}
        titleAlign="left"
        className={selection.paperId ? 'text-xl text-primary' : 'text-primary'}
      >
        {!selection.subject && (
          <p className="text-sm text-gray-600 text-left -mt-4">
            Select a past paper to complete under exam conditions and receive feedback
          </p>
        )}
      </PageHeader>

      {/* Content */}
      <div className="flex-1 flex flex-col gap-6 overflow-hidden p-2">
        {/* In-Progress Banner */}
        {inProgressAttempt && (
          <div className="flex-shrink-0 mt-4">
            <InProgressBanner attempt={inProgressAttempt} />
          </div>
        )}

        {/* Selection Flow */}
        {!inProgressAttempt && (
          <div className="flex-shrink-0 mt-4">
            {!selection.subject && renderSubjectSelection()}
            {selection.subject && !selection.paperNumber && renderPaperNumberSelection()}
            {selection.subject && selection.paperNumber && !selection.paperId && renderPaperList()}
            {selection.paperId && renderPaperPreview()}
          </div>
        )}

        {/* PDF Viewer - Shown when paper is selected, takes remaining space */}
        {selection.paperId && (
          <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden">
            {/* PDF Viewer with scrollbar */}
            <div className="h-full overflow-y-auto p-6 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-orange-500 [&::-webkit-scrollbar-thumb]:rounded-full">
              {pdfUrl ? (
                <div className="flex flex-col items-center gap-4">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center min-h-[400px]">
                        <p className="text-gray-500">Loading PDF...</p>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center min-h-[400px]">
                        <p className="text-red-500">Failed to load PDF</p>
                      </div>
                    }
                  >
                    {Array.from(new Array(numPages), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        className="mb-4 shadow-md"
                        width={Math.min(window.innerWidth * 0.7, 800)}
                      />
                    ))}
                  </Document>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">PDF not available</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Progress Bar (only shown when paper is NOT selected) */}
      {/* {!selectedPaper && (
        <div className="bg-primary p-6 flex items-center justify-between rounded-2xl">
          <p className="text-white text-lg">Complete all past papers to unlock custom mocks</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-white bg-opacity-30 rounded-full overflow-hidden">
                <div className="w-1/2 h-full bg-[#2B3440] rounded-full"></div>
              </div>
              <span className="text-white font-medium">50%</span>
            </div>
            <Button className="bg-white bg-opacity-20 border-2 border-white text-white hover:bg-white hover:bg-opacity-30">
              Generate Mock <LockClosedIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )} */}

      {/* Usage Limit Error Modal */}
      {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
    </>
  );
};

export default FullPastPapersPage;
